import { formatToTime } from "./FormatToDateTime";
import { formatDate } from "./Master";

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Prints the student attendance list as a proper report (not a screenshot).
 * Returns false if nothing to print or the pop-up was blocked.
 */
export const printStudentAttendance = ({
  rows,
  attendanceType,
  date,
  sessionName,
  courseName,
  batchName,
  teacherName,
  filterLabel,
  counts,
}) => {
  if (!rows?.length) return false;

  const isInOut = attendanceType === "in_out";

  const tableRows = rows
    .map((item, index) => {
      const att = item?.today_attendance;
      const checkIn = Number(att?.check_in);

      let status = "Not Marked";
      let statusClass = "nm";

      if (checkIn === 1) {
        status = "Present";
        statusClass = "p";
      } else if (checkIn === 2) {
        status = "Absent";
        statusClass = "a";
      }

      const inTime = att?.check_in_time
        ? esc(formatToTime(att.check_in_time))
        : "-";
      const outTime = att?.check_out_time
        ? esc(formatToTime(att.check_out_time))
        : "-";

      return `
<tr>
  <td>${index + 1}</td>
  <td>${esc(item?.registration_no) || "-"}</td>
  <td class="left"><b>${esc(item?.name)}</b></td>
  <td>${esc(item?.phone) || "-"}</td>
  <td>${esc(item?.latest_course_and_fee?.batch?.title) || "-"}</td>
  <td class="${statusClass}">${status}</td>
  ${isInOut ? `<td>${inTime}</td><td>${outTime}</td>` : ""}
</tr>`;
    })
    .join("");

  const meta = [
    ["Session", sessionName],
    ["Course", courseName],
    ["Batch", batchName],
    ["Class Teacher", teacherName],
    ["Showing", filterLabel],
  ]
    .filter(([, v]) => v)
    .map(([k, v]) => `<span><b>${k}:</b> ${esc(v)}</span>`)
    .join("");

  const printWindow = window.open("", "_blank");

  if (!printWindow) return false;

  printWindow.document.write(`
<html>
<head>
<title>Student Attendance</title>
<style>
  @page { size: A4 portrait; margin: 10mm; }

  * { box-sizing: border-box; }

  body {
    font-family: Arial, sans-serif;
    font-size: 11px;
    color: #000;
    margin: 0;
    padding: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  h2 { text-align: center; margin: 0 0 4px; font-size: 18px; }
  h4 { text-align: center; margin: 0 0 12px; font-weight: normal; font-size: 12px; }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 20px;
    justify-content: center;
    margin-bottom: 10px;
  }

  .summary {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  .summary div {
    border: 1px solid #000;
    padding: 4px 14px;
    text-align: center;
    min-width: 90px;
  }

  .summary b { display: block; font-size: 14px; }

  table { width: 100%; border-collapse: collapse; }

  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }

  th, td {
    border: 1px solid #000;
    padding: 5px 6px;
    text-align: center;
  }

  th { background: #efefef; }
  td.left { text-align: left; }

  td.p { font-weight: bold; color: #1a7f37; }
  td.a { font-weight: bold; color: #c62828; }
  td.nm { color: #666; }

  .footer {
    margin-top: 40px;
    display: flex;
    justify-content: space-between;
  }

  .sign {
    border-top: 1px solid #000;
    padding-top: 4px;
    width: 180px;
    text-align: center;
  }

  .printed {
    margin-top: 14px;
    font-size: 9px;
    color: #555;
    text-align: right;
  }
</style>
</head>
<body>

<h2>Student Attendance Register</h2>
<h4>${esc(formatDate(date))} &nbsp;|&nbsp; ${isInOut ? "IN / OUT" : "Present / Absent"}</h4>

<div class="meta">${meta}</div>

<div class="summary">
  <div><b>${esc(counts?.total ?? 0)}</b>Total</div>
  <div><b>${esc(counts?.present ?? 0)}</b>Present</div>
  <div><b>${esc(counts?.absent ?? 0)}</b>Absent</div>
  <div><b>${esc(counts?.notMarked ?? 0)}</b>Not Marked</div>
</div>

<table>
  <thead>
    <tr>
      <th style="width:30px">SN</th>
      <th>Reg No</th>
      <th>Name</th>
      <th>Mobile</th>
      <th>Batch</th>
      <th>Status</th>
      ${isInOut ? "<th>IN</th><th>OUT</th>" : ""}
    </tr>
  </thead>
  <tbody>
    ${tableRows}
  </tbody>
</table>

<div class="footer">
  <div class="sign">Class Teacher</div>
  <div class="sign">Principal</div>
</div>

<div class="printed">Printed on ${esc(new Date().toLocaleString("en-IN"))}</div>

<script>
  window.onload = function () {
    window.print();
    window.onafterprint = function () {
      window.close();
    };
  };
</script>

</body>
</html>
`);

  printWindow.document.close();

  return true;
};
